/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable no-param-reassign */
/* eslint-disable object-curly-newline */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Collapse, IconButton, styled } from '@mui/material';
import Alert from '@mui/material/Alert';
import { isEmpty, isNull, values } from 'lodash';
import { BENCHMARK_MULTIPLE_VALUE_ALIAS, FINANCIALS_METRIC_VALUE_ALIAS } from 'common/constants/allocations';
import { FIX_BEFORE_CONTINUE } from 'common/constants/messages/validations';
import { MessageBox } from 'components';
import { ConfirmationDelete, ConfirmationDialog } from 'components/Dialogs';
import { GridSkeleton } from 'components/Grid';
import ScalarDatasheet from 'components/ScalarDatasheet';
import { ValueEditor, ValueViewer } from 'components/Spreadsheet/components';
import Cell from 'components/Spreadsheet/components/Cell';
import { DialogContext, LedgerContext } from 'context';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { useTableValidation } from 'services/hooks';
import { formatNumbers, Logger, objToArray, onCellsChanged } from 'utilities';
import { AddRowButton, DeleteRowButton } from './components';
import { CellChanged, GetConfigToUseProps, LedgerTableProps, RowProps } from './LedgerTable.props';
import { classes, StyledLedgerContextProvider } from './LedgerTable.styles';


const ContentComponent = () => <ConfirmationDelete itemName="row" />;

const LedgerTable = ({
  id,
  cell: parentCell,
  colConfig,
  rowConfig = [],
  rows = [],
  tableTerms,
  isAlertVisible = false,
  setIsAlertVisible,
  showAddRowButton = true,
  showDeleteColumn = true,
  cells,
  setCells,
  linkedCellUpdates,
  addRow,
  deleteRow,
  showTotalRow,
  doFullValidation,
  setIsValid,
  updateValidationStatus,
  hasRowTitles = false,
  ledgerScope = {},
  disabled = false,
  conditions,
  tableData,
  getKey,
  tableId = '',
  sharedCells = {},
  customValidations,
  currencyCode,
  currency,
  addBtnAlign = 'left',
  inLedger,
  exchangeRateMap,
}: LedgerTableProps) => {
  const { showDialog } = useContext(DialogContext);
  const { validateCells } = useTableValidation();
  // this is what is rendered as cells
  const [ledgerGrid, setLedgerGrid] = useState<any>();

  if (!getKey || (typeof getKey === 'object' && !isNull(getKey) && isEmpty(getKey))) {
    getKey = (alias: string, colIndex: number, tId: string, rowIndex: number) => `${alias}${rowIndex + 1}`;
  }

  const applyExpressions = async (changes: CellChanged[], cells: ICell[]) => {
    const computedCells = await onCellsChanged(changes, cells);
    return computedCells;
  };

  const applyValidations = async (changes: CellChanged[], cells: { [key: string]: ICell }) => {
    const tmpCells = { ...cells };
    // Prepare data to be validated
    const cellsToValidate = changes.map(change => cells[change.cell.key as keyof typeof cells]) as ICell[];

    const { validatedCells } = await validateCells({
      cellsToValidate,
      tableData,
    });

    // Update cells
    validatedCells.forEach((cell: ICell) => {
      const { key } = cell;
      if (typeof key === 'string') {
        tmpCells[key] = cell;
      }
    });

    return tmpCells;
  };

  const applyConditions = (cells: any) => {
    const tmpState = { ...cells };

    if (cells && conditions) {
      const tmpCells = objToArray(cells).map(([, cell]) => cell) as ICell[];
      tmpCells.forEach(cell =>
        conditions({
          cell,
          tableData,
          parentCell,
          tableCells: tmpState,
          onCellsChanged,
        })
      );
    }
    return tmpState;
  };

  const validateAll = async () => {
    if (updateValidationStatus) {
      // begin validation proccess
      updateValidationStatus(true);
    }

    const tmpCells = { ...cells };

    const cellsToValidate: ICell[] = [];

    rows.forEach((row: any, rowIndex: number) => {
      colConfig.forEach((col: any, colIndex: number) => {
        const alias = rowConfig.length ? row.alias : col.alias;
        const key = getKey(alias, colIndex, tableId, rowIndex, row.alias);
        const tmpCell = {
          ...col, // Copy config data
          ...cells[key],
        };
        cellsToValidate.push(tmpCell);
      });
    });

    let isValid = true;
    if (cellsToValidate.length > 0) {
      // Validate cells
      const { validatedCells, areCellsValid } = await validateCells({ cellsToValidate, tableData, customValidations });

      isValid = areCellsValid;

      // Update cells
      validatedCells.forEach(cell => {
        tmpCells[cell.key as keyof typeof tmpCells] = cell;
      });
      setCells((state: any) => ({ ...state, ...tmpCells }));
    }

    if (setIsValid) {
      await setIsValid(isValid);
    }
    if (updateValidationStatus) {
      // finish validation proccess
      updateValidationStatus(false);
    }
  };

  const validateAllAsync = async () => {
    if (doFullValidation) {
      try {
        await validateAll();
      } catch (error: any) {
        Logger.error('Error validating all cells', error);
        throw new Error(error);
      }
    }
  };

  // check if we have to validate all cells on the table
  useEffect(() => {
    validateAllAsync();
  }, [doFullValidation]);

  const onCellsChange = async (changes: any) => {
    let tmpCells = { ...cells, ...sharedCells };

    // Get linked cell updates, not defined in all ledgers
    const updatedChanges = typeof linkedCellUpdates === 'function' ? linkedCellUpdates(changes, tmpCells) : changes;

    // Apply expressions
    tmpCells = await applyExpressions(updatedChanges, tmpCells);

    // Apply conditions
    tmpCells = applyConditions(tmpCells);
    // decide if it's necessary to re-evaluate expresions
    const metricOrMultipleWasSelected
      = [BENCHMARK_MULTIPLE_VALUE_ALIAS, FINANCIALS_METRIC_VALUE_ALIAS].includes(changes[0].cell.alias)
      && changes[0].col === 0;
    if (metricOrMultipleWasSelected) {
      tmpCells = await applyExpressions(updatedChanges, tmpCells);
    }
    // Apply validations
    tmpCells = await applyValidations(updatedChanges, tmpCells);

    setCells((state: any) => ({ ...state, ...tmpCells }));
  };

  const totalRow = useMemo(() => {
    const getTotal = (alias: string) => {
      const cellValues = values(cells).filter(cell => cell.alias === alias && cell.insideLedger) || [];

      return cellValues.reduce((total, item) => {
        total += parseFloat(`${Number(item.value)}`);
        return total;
      }, 0);
    };

    if (colConfig) {
      return colConfig.map((col: any, index: number) => {
        const totalValue = col.hasTotal ? getTotal(col.alias) : '-';
        const value = index === 0 ? 'Total' : totalValue;
        const { valueViewer, ...colProps } = col;

        return {
          ...colProps,
          key: `total${index + 1}`,
          className: 'subtitle align-left',
          readOnly: true,
          forceComponent: false,
          component: null,
          value: formatNumbers({
            value,
            currencyCode,
            format: col.format,
            currency,
            prefix: null,
            suffix: null,
            allowNegativeValue: null,
            blankIfNull: null,
            staticCurrency: null,
          }),
        };
      });
    }
  }, [colConfig, cells]);

  const generateTableGrid = () => {
    let grid = rows.map((row: any, rowIndex: number) =>
      colConfig.map((col: any, colIndex: number) => {
        let key = '';

        key = getKey(rowConfig.length ? row.alias : col.alias, colIndex, tableId, rowIndex);

        const cell = cells[key] || {};
        const { selectorInLabelColumn } = cell;

        const isNotMetricOrMultipleRow = ![BENCHMARK_MULTIPLE_VALUE_ALIAS, FINANCIALS_METRIC_VALUE_ALIAS].includes(
          row.alias
        );
        const isNotMetricOrMultipleTitleCell = cell.colAlias === 'title' && isNotMetricOrMultipleRow;
        const isMetricOrMultipleValueCell = cell.colAlias === 'value' && !isNotMetricOrMultipleRow;

        const config = getConfigToUse({
          rowConfig,
          shouldUseTitle: isNotMetricOrMultipleTitleCell || isMetricOrMultipleValueCell,
          rowIndex,
          fallbackConfig: col,
        });

        // Copy config data and replace the common properties with cell data
        const configAndCell = { ...config, ...cell };

        const determineIfReadOnly
          = cell?.readOnly || (col?.readOnly && isNotMetricOrMultipleRow) || config?.readOnly || disabled;

        const tmpCell = {
          ...configAndCell,
          currency,
          readOnly: selectorInLabelColumn ? false : determineIfReadOnly,
          // Style can be present either in the cell of in the column config
          currencyCode: configAndCell?.format?.style === 'currency' ? currencyCode : null,
        };
        // Sometimes row will have a value and the cell will not exist yet.
        // It's better than displaying col title as value in some components.
        if (isEmpty(cell) && row[col?.alias]) {
          tmpCell.value = row[col.alias];
        }

        // Add component
        if (tmpCell.component) {
          tmpCell.component = React.cloneElement(tmpCell.component, { cell: tmpCell }, null);
        }

        return tmpCell;
      })
    );

    if (showTotalRow) {
      grid = [...grid, totalRow];
    }
    setLedgerGrid(grid);
  };

  const getConfigToUse = ({ rowConfig, shouldUseTitle, rowIndex, fallbackConfig }: GetConfigToUseProps) => {
    if (rowConfig.length) {
      if (shouldUseTitle) {
        const { dataEditor, valueViewer, ...rowConfigTitle } = rowConfig[rowIndex];
        return rowConfigTitle;
      }
      return rowConfig[rowIndex];
    }
    return fallbackConfig;
  };

  const tableHeader = useMemo(() => {
    const rowColumns = [...colConfig];

    return rowColumns.map(col => ({
      value: col.value,
      className: `table-header ${col.className || ''}`,
      width: col.width || '150px',
      readOnly: true,
      disableEvents: true,
    }));
  }, [colConfig]);

  useEffect(() => {
    if (isEmpty(cells)) {
      setLedgerGrid([]);
    } else {
      generateTableGrid();
    }
  }, [cells, rows]);

  useEffect(() => {
    if (rows) {
      const tmpCells = applyConditions(cells);
      setCells((state: any) => ({ ...state, ...tmpCells }));
    }
  }, [rows]);

  const renderValue = (cell: any) => {
    if (cell.className && cell.className === 'table-header') {
      return cell.value;
    }
    return formatNumbers(cell);
  };

  const ledgerTableContext = useMemo(
    () => ({
      parentCell,
      cells,
      setCells,
      rows,
      colConfig,
      onCellsChange,
      exchangeRateMap,
    }),
    [parentCell, cells, setCells, rows, colConfig, onCellsChange, exchangeRateMap]
  );

  const handleDeleteRow = (rowIndex: number) => {
    if (showDeleteColumn) {
      showDialog({
        wrapper: ConfirmationDialog,
        content: ContentComponent,
        actions: [
          {
            label: 'Cancel',
            type: 'cancel',
          },
          {
            label: 'Delete',
            variant: 'contained',
            type: 'danger',
            callback: () => deleteRow?.(rowIndex),
          },
        ],
      });
    } else {
      deleteRow?.(rowIndex);
    }
  };

  const Row = ({ row, children }: RowProps) => (
    <tr className={classes.row}>
      {children}
      {showDeleteColumn && row > 0 && row <= rows.length && !disabled && (
        <td className="delete-row">
          <DeleteRowButton rowIndex={row - 1} deleteRow={handleDeleteRow} />
        </td>
      )}
    </tr>
  );

  if (!ledgerGrid) {
    return (
      <div style={{ minWidth: '500px' }}>
        <GridSkeleton maxRows={3} maxColumns={colConfig.length + 1} />
      </div>
    );
  }

  if (isEmpty(ledgerGrid)) {
    return (
      <MessageBox
        title={`The ${tableTerms?.tableName} table has no data yet`}
        action={<AddRowButton addRow={addRow} tableTerms={tableTerms} disabled={disabled} />}
        showIcon={false}
        hasFrame={false}
      />
    );
  }

  return (
    <StyledLedgerContextProvider value={ledgerTableContext}>
      <Collapse in={isAlertVisible}>
        <Alert
          variant="outlined"
          severity="error"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                setIsAlertVisible?.(false);
              }}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }>
          {FIX_BEFORE_CONTINUE}
        </Alert>
        <br />
      </Collapse>
      <div id={id}>
        <ScalarDatasheet
          className="white-table-header"
          data={[tableHeader, ...ledgerGrid]}
          dataRenderer={cell => cell.value}
          valueRenderer={renderValue}
          onCellsChanged={onCellsChange}
          valueViewer={ValueViewer}
          dataEditor={ValueEditor}
          rowRenderer={Row}
          cellRenderer={(props: any) => <Cell {...props} inLedger={inLedger} />}
        />
      </div>
      {showAddRowButton && !disabled ? (
        <>
          <br />
          <div style={{ textAlign: addBtnAlign }}>
            <AddRowButton addRow={addRow} tableTerms={tableTerms} />
          </div>
        </>
      ) : null}
    </StyledLedgerContextProvider>
  );
};

export default LedgerTable;
